import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import useInView from 'react-cool-inview';

// Module components
import ErrorHandler from '../../_molecules/ErrorHandler';
import TrackPageView from '@tracking/TrackPageView';
import MemberProfileCard from './MemberProfileCard';
import MemberProfileDetailsCard from './MemberProfileDetailsCard';
import FavoriteButtonMember from './FavoriteButtonMember';
import MemberRecentActivityTabs from './MemberRecentActivityTabs';
import MemberSearchSimiliar from './MemberSearchSimiliar';
import MemberRecognitionList from './MemberRecognitionList';

// Shared components
import LoadingArea from '@molecules/LoadingArea';
import { H3, H4, SectionWrapperInner, SectionWrapperOuter, DivWithShadowAlternate } from '@styled';

// Functionality
import useIsContactFavorite from '@hooks/useIsContactFavorite';
import useMemberFull from '@hooks/useMemberFull';
import useDocumentTitle from '@hooks/useDocumentTitle';

const SectionContainerStyled = styled.div`
    margin-bottom: 50px;
`;

const MemberProfile = ({ graphID }) => {
    const { observe, inView } = useInView({ unobserveOnEnter: true });

    const isFavorite = useIsContactFavorite(graphID);
    const { data: memberData, loading } = useMemberFull(graphID);
    const { firstName, lastName } = memberData || {};
    const { documentTitle } = useDocumentTitle(`${firstName} ${lastName}`);

    if (!memberData || loading) {
        return <LoadingArea />;
    }

    return (
        <ErrorHandler>
            <TrackPageView>
                <SectionWrapperOuter>
                    <SectionWrapperInner>
                        <div className="fade-in-fast pb-4">
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <H3 className="mt-2 mb-3">{documentTitle}</H3>
                                <div style={{ display: 'flex' }}>
                                    {typeof isFavorite !== 'undefined' && (
                                        <span style={{ marginLeft: '18px' }}>
                                            <FavoriteButtonMember
                                                recordID={memberData.recordId}
                                                isFavorite={isFavorite}
                                            />
                                        </span>
                                    )}
                                </div>
                            </div>

                            <SectionContainerStyled className="flex-wrap row">
                                <div className="col-lg-4 d-flex mb-4 mb-lg-0">
                                    <MemberProfileCard graphID={graphID} />
                                </div>

                                <div className="col-lg-8 d-flex flex-column">
                                    <MemberProfileDetailsCard graphID={graphID} isFavorite={isFavorite} />
                                </div>
                            </SectionContainerStyled>

                            <SectionContainerStyled>
                                <H4 className="mb-1.5rem">Recent Activity</H4>
                                <MemberRecentActivityTabs graphID={graphID} />
                            </SectionContainerStyled>

                            {memberData.recognition.length > 0 && (
                                <SectionContainerStyled>
                                    <div className="fade-in-fast">
                                        <H4 className="mb-1.5rem">{memberData.firstName}'s Recognition</H4>
                                        <DivWithShadowAlternate style={{ width: '100%' }}>
                                            <div className="p-4">
                                                <MemberRecognitionList graphID={graphID} />
                                            </div>
                                        </DivWithShadowAlternate>
                                    </div>
                                </SectionContainerStyled>
                            )}

                            <SectionContainerStyled ref={observe}>
                                {inView && (
                                    <div className="fade-in-fast">
                                        <H4 className="mb-1rem">Members Similar to {memberData.firstName}</H4>
                                        <MemberSearchSimiliar graphID={graphID} />
                                    </div>
                                )}
                            </SectionContainerStyled>
                        </div>
                    </SectionWrapperInner>
                </SectionWrapperOuter>
            </TrackPageView>
        </ErrorHandler>
    );
};

MemberProfile.propTypes = {
    graphID: PropTypes.string,
};

export default MemberProfile;
